<div class="rfid-price-container">
  <div class="rfid-price-page">
    <div class="page-header">
      <div class="title-section">
        <div class="title-row">
          <span class="material-symbols-rounded page-icon">contactless</span>
          <h1 class="title-large" data-testid="rfidPriceTitle">RFID Price Management</h1>
        </div>
        <p class="body-medium text-muted">
          View and update current RFID keycard prices stored in Firestore.
        </p>
      </div>
    </div>

    @if (isLoading()) {
      <div class="loading-state">
        <mat-spinner diameter="44"></mat-spinner>
        <div class="body-medium text-muted">Loading RFID prices from Firestore...</div>
      </div>
    } @else if (errorMessage()) {
      <div class="error-banner">
        <span class="material-symbols-rounded">error</span>
        <div>{{ errorMessage() }}</div>
      </div>
    } @else {
      <div class="cards-grid">
        <!-- Energy Subscription Card -->
        <div class="card price-card" data-testid="energyCard">
          <div class="card-header">
            <div class="icon-bubble energy-bubble">
              <span class="material-symbols-rounded">bolt</span>
            </div>
            <div class="card-titles">
              <div class="subscription-tag">Subscription</div>
              <div class="title-medium">Energy</div>
            </div>
          </div>

          <p class="body-small text-muted card-description">
            Current RFID keycard price for users subscribed to the <strong>Energy</strong> plan.
          </p>

          <div class="field-group">
            <label for="energyInput" class="field-label">Price</label>
            <div class="input-wrapper">
              <span class="material-symbols-rounded input-icon">sell</span>
              <input
                id="energyInput"
                type="text"
                class="price-input"
                [(ngModel)]="energyPrice"
                placeholder="e.g. 50 sek"
                data-testid="energyPriceInput"
              />
            </div>
          </div>

          <div class="preview-pill">
            <span class="text-muted">Live value:</span>
            <span class="preview-value" data-testid="energyLiveValue">{{ energyPrice() || '(empty)' }}</span>
          </div>
        </div>

        <!-- Flexible Subscription Card -->
        <div class="card price-card" data-testid="flexibleCard">
          <div class="card-header">
            <div class="icon-bubble flexible-bubble">
              <span class="material-symbols-rounded">tune</span>
            </div>
            <div class="card-titles">
              <div class="subscription-tag">Subscription</div>
              <div class="title-medium">Flexible</div>
            </div>
          </div>

          <p class="body-small text-muted card-description">
            Current RFID keycard price for users subscribed to the <strong>Flexible</strong> plan.
          </p>

          <div class="field-group">
            <label for="flexibleInput" class="field-label">Price</label>
            <div class="input-wrapper">
              <span class="material-symbols-rounded input-icon">sell</span>
              <input
                id="flexibleInput"
                type="text"
                class="price-input"
                [(ngModel)]="flexiblePrice"
                placeholder="e.g. 100 sek"
                data-testid="flexiblePriceInput"
              />
            </div>
          </div>

          <div class="preview-pill">
            <span class="text-muted">Live value:</span>
            <span class="preview-value" data-testid="flexibleLiveValue">{{ flexiblePrice() || '(empty)' }}</span>
          </div>
        </div>
      </div>

      <!-- Additional Custom Fields (if needed) -->
      <div class="card custom-fields-card">
        <div class="custom-card-header">
          <div>
            <div class="title-medium">Additional Pricing Fields</div>
            <div class="body-small text-muted">
              Add or manage custom pricing keys stored under <code>prices</code> in Firestore.
            </div>
          </div>
          <button type="button" class="secondary-button add-field-btn" (click)="addCustomField()" data-testid="addFieldBtn">
            <span class="material-symbols-rounded">add</span>
            Add Field
          </button>
        </div>

        @if (customPrices().length === 0) {
          <div class="no-custom-fields body-small text-muted">
            No custom pricing fields configured. Energy and Flexible are the primary fields.
          </div>
        } @else {
          <div class="custom-fields-list">
            @for (item of customPrices(); track $index) {
              <div class="custom-field-row" [attr.data-testid]="'customFieldRow_' + $index">
                <input
                  type="text"
                  class="custom-key-input"
                  placeholder="Field name (e.g. dropIn)"
                  [value]="item.key"
                  (input)="updateCustomKey($index, $any($event.target).value)"
                  [attr.data-testid]="'customKeyInput_' + $index"
                />
                <input
                  type="text"
                  class="custom-value-input"
                  placeholder="Price (e.g. 150 sek)"
                  [value]="item.value"
                  (input)="updateCustomValue($index, $any($event.target).value)"
                  [attr.data-testid]="'customValueInput_' + $index"
                />
                <button
                  type="button"
                  class="icon-button delete-btn"
                  (click)="removeCustomField($index)"
                  title="Remove field"
                  [attr.data-testid]="'removeCustomFieldBtn_' + $index"
                >
                  <span class="material-symbols-rounded">delete</span>
                </button>
              </div>
            }
          </div>
        }
      </div>

      <!-- Document Preview / Schema Verification -->
      <div class="card preview-card">
        <div class="preview-header">
          <span class="material-symbols-rounded">data_object</span>
          <span class="title-small">Firestore Document Payload Preview</span>
        </div>
        <pre class="json-code"><code>{{ previewPayload() | json }}</code></pre>
      </div>
    }
  </div>

  <!-- Sticky Save / Action Bar -->
  <div class="action-bar">
    <div class="action-bar-inner">
      <div class="change-status">
        @if (hasChanges()) {
          <span class="status-indicator has-changes"></span>
          <span class="body-medium">You have unsaved changes</span>
        } @else {
          <span class="status-indicator synced"></span>
          <span class="body-medium text-muted">Synced with Firestore</span>
        }
      </div>

      <div class="action-buttons">
        <button
          type="button"
          class="secondary-button"
          (click)="resetForm()"
          [disabled]="!hasChanges() || isSaving()"
          data-testid="resetButton"
        >
          Discard Changes
        </button>

        <button
          type="button"
          class="primary-button"
          (click)="savePrices()"
          [disabled]="!hasChanges() || isSaving()"
          data-testid="savePricesButton"
        >
          @if (isSaving()) {
            <mat-spinner diameter="20" color="accent"></mat-spinner>
            <span>Saving...</span>
          } @else {
            <span class="material-symbols-rounded">save</span>
            <span>Update Prices</span>
          }
        </button>
      </div>
    </div>
  </div>
</div>
